import type { AdminPayment, PaymentMethod, PaymentStatus } from '../api/schemas';

/** How the admin panel names a way to pay and where a payment stands. */
export const METHOD_LABEL: Readonly<Record<PaymentMethod, string>> = {
  yappy: 'Yappy',
  efectivo: 'Efectivo',
  transferencia: 'Transferencia',
  tarjeta: 'Tarjeta',
  paypal: 'PayPal',
  stripe: 'Tarjeta (Stripe)',
  apple: 'App Store',
};

export const STATUS_LABEL: Readonly<Record<PaymentStatus, string>> = {
  pendiente: 'Sin completar',
  reportado: 'Por confirmar',
  confirmado: 'Confirmado',
  fallido: 'Fallido',
  rechazado: 'Rechazado',
};

const PLAN_WORD: Readonly<Record<string, string>> = { reservas: 'Reservas', base: 'Base', pro: 'Pro', max: 'Max' };
const CYCLE_WORD = { monthly: 'mensual', annual: 'anual' } as const;

/** "Pro anual" for a checkout row; empty for an admin row that names no plan. */
export function planCycleLabel(payment: Pick<AdminPayment, 'plan' | 'billingCycle'>): string {
  if (!payment.plan) return '';
  const plan = PLAN_WORD[payment.plan] ?? payment.plan;
  return payment.billingCycle ? `${plan} ${CYCLE_WORD[payment.billingCycle]}` : plan;
}

/** A row from before the checkout carries no status: an admin recorded money that arrived. */
export function statusOf(payment: Pick<AdminPayment, 'status'>): PaymentStatus {
  return payment.status ?? 'confirmado';
}

/** Money only: what the ledger and its totals are made of. */
export function confirmedOnly(payments: readonly AdminPayment[]): AdminPayment[] {
  return payments.filter((payment) => statusOf(payment) === 'confirmado');
}

/** How long an open card or PayPal attempt stays listed: long after any link it opened, short enough that abandoned ones fall away. */
export const OPEN_BANK_ATTEMPT_DAYS = 30;
const DAY_MS = 24 * 60 * 60 * 1000;
const BANK_METHODS: readonly PaymentMethod[] = ['tarjeta', 'paypal'];

/**
 * Card and PayPal attempts that reached the bank and nobody has confirmed
 * yet, newest first: what an admin can ask the bank about.
 */
export function openBankAttempts(payments: readonly AdminPayment[], nowMs: number): AdminPayment[] {
  const since = nowMs - OPEN_BANK_ATTEMPT_DAYS * DAY_MS;
  return payments
    .filter(
      (payment) =>
        payment.status === 'pendiente' &&
        BANK_METHODS.includes(payment.method) &&
        Boolean(payment.providerRef) &&
        Date.parse(payment.createdAt) >= since,
    )
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt));
}
